import { ActionIcon, Anchor, Breadcrumbs, Button, Menu, Modal, Tabs, rem } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import type { RunStatus } from '@prisma/client';
import {
  IconArrowLeft,
  IconBrandGithub,
  IconClockFilled,
  IconDotsVertical,
  IconDownload,
  IconExclamationCircle,
  IconPlayerPlayFilled,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import EditableTextArea from '~/components/common/EditableTextArea';
import EditableTitle from '~/components/common/EditableTitle';
import { HeadTitle } from '~/components/common/HeadTitle';
import StatusIcon from '~/components/icons/StatusIcon';
import TestSchedule from '~/components/test/TestSchedule';
import CanNotStartTesting from '~/components/testSuite/CanNotStartTesting';
import CIOptions from '~/components/testSuite/CIOptions';
import ManualTriggerAdvanced from '~/components/testSuite/ManualTriggerAdvanced';
import { TestSuiteHeatMap } from '~/components/testSuite/heatmap/TestSuiteHeatMap';
import SuiteSettings from '~/components/testSuite/SuiteSettings';
import { TestSuiteResultView } from '~/components/testSuite/TestSuiteResultView';
import TestSuiteTestsContainer from '~/components/testSuite/TestSuiteTestsView';
import { TestRunDrawer } from '~/components/testRun/TestRunDrawer';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useQueryParam } from '~/hooks/nav/useQueryParam';
import { queryParamToString } from '~/logic/query-param';
import { downloadTestSuiteCsv } from '~/logic/file/csv-export';
import { api } from '~/trpc-extra/api';

export default function TestSuitePage() {
  const { isAdmin } = useAuthData();
  const trpcUtils = api.useUtils();
  const router = useRouter();
  const projectId = useProjectId();
  const testSuiteId = queryParamToString(router.query.testSuiteId);
  const [settingsModalOpened, setSettingsModalOpened] = useState(false);
  const { data: schedule } = api.testSchedule.get.useQuery({ testSuiteId });
  const { mutate: deleteSuite } = api.testSuite.delete.useMutation({
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        message: `Test suite deleted`,
        icon: <IconTrash />,
      });
      trpcUtils.testSuite.list.invalidate().catch(() => undefined);
      router.push(`/project/${projectId}/testSuite`).catch(() => undefined);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({
        color: 'red',
        title: 'Deletion Failed',
        message: `${e}`,
        icon: <IconExclamationCircle />,
      });
    },
  });
  const { mutateAsync: updateSuite } = api.testSuite.update.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Saving...', color: 'gray' });
    },
    onSuccess: () => {
      trpcUtils.testSuite.get.invalidate().catch(() => undefined);
      notifications.show({ message: 'Update Saved', color: 'blue' });
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Failed to update', color: 'red' });
    },
  });

  const { data: testSuite } = api.testSuite.get.useQuery({
    projectId: projectId!,
    testSuiteId,
  });

  const { data: canRunTests, isLoading: canRunTestsLoading } = api.project.getCanRunTests.useQuery({
    projectId: projectId!,
  });

  const clearRunHistory = api.testSuite.clearRunHistory.useMutation();

  const { refetch: fetchExportData, isFetching: isExportLoading } = api.testSuite.getCsvExportData.useQuery(
    { projectId: projectId!, testSuiteId },
    { enabled: false },
  );

  const [latestRunStatus, setLatestRunStatus] = useState<RunStatus>();

  const [tab, setTab] = useQueryParam('tab', 'tests');

  const navs = useMemo(
    () => [
      <Anchor
        href={`/project/${projectId || ''}/testSuite`}
        key="TestSuite"
        component={Link}
        className="text-sm text-zinc-700 dark:text-zinc-400"
      >
        <div className="flex items-center">
          <ActionIcon
            variant="transparent"
            title="Back"
            size={15}
            onClick={() => router.push(`/project/${projectId || ''}/testSuite`).catch(() => undefined)}
            className="mr-1 flex justify-start text-gray-700 dark:text-gray-400"
          >
            <IconArrowLeft size={20} />
          </ActionIcon>
          <span> Test Suites</span>
        </div>
      </Anchor>,
    ],
    [projectId, router],
  );

  return (
    <>
      <HeadTitle
        title={testSuite?.name}
        metric={`${testSuite?.suiteTests.length} tests`}
        suffix="Suite"
      />
      <div className="flex h-full flex-col px-8 py-0">
        <Breadcrumbs
          separator=">"
          mt="sm"
          className="mb-2 text-xs"
        >
          {navs}
        </Breadcrumbs>
        <div className="flex h-8 items-center justify-between gap-8">
          <EditableTitle
            title={testSuite?.name || 'Loading...'}
            tooltip="Click to edit the test suite name"
            handleSave={async (newName) => {
              await updateSuite({ id: testSuiteId, projectId: projectId!, data: { name: newName } });
            }}
          />

          <div className="flex flex-row items-center gap-2">
            <Button
              variant="default"
              leftSection={<IconClockFilled style={{ width: rem(14), height: rem(14) }} />}
              onClick={() => {
                if (canRunTestsLoading) {
                  notifications.show({
                    message: 'Loading...Please try again in a few seconds',
                    color: 'gray',
                  });
                } else if (!canRunTests) {
                  modals.open({
                    size: 'sm',
                    centered: true,
                    children: <CanNotStartTesting />,
                  });
                } else {
                  modals.open({
                    title: schedule ? 'Update Test Schedule' : 'Create Test Schedule',
                    size: 'lg',
                    centered: true,
                    children: (
                      <TestSchedule
                        selectedCronTab={schedule?.cronTab || undefined}
                        selectedTimezoneIana={schedule?.timeZoneIana || undefined}
                        testSuiteId={testSuiteId}
                        onSubmit={() => modals.closeAll()}
                        onOpenSuiteSettings={() => {
                          modals.closeAll();
                          setSettingsModalOpened(true);
                        }}
                      />
                    ),
                  });
                }
              }}
            >
              {schedule ? 'Modify Schedule' : 'Schedule'}
            </Button>
            <Button
              variant="default"
              leftSection={<IconBrandGithub style={{ width: rem(14), height: rem(14) }} />}
              onClick={() => {
                if (canRunTestsLoading) {
                  notifications.show({
                    message: 'Loading...Please try again in a few seconds',
                    color: 'gray',
                  });
                } else if (!canRunTests) {
                  modals.open({
                    size: 'sm',
                    centered: true,
                    children: <CanNotStartTesting />,
                  });
                } else {
                  modals.open({
                    size: '60%',
                    title: <div className="w-full justify-center pl-4 font-semibold">Add to CI</div>,
                    children: <CIOptions testSuiteId={testSuiteId} />,
                  });
                }
              }}
            >
              Add to CI
            </Button>
            {!!testSuite && (
              <Button
                variant="default"
                onClick={() => setSettingsModalOpened(true)}
                leftSection={<IconSettings style={{ width: rem(14), height: rem(14) }} />}
              >
                Settings
              </Button>
            )}
            <Button
              leftSection={<IconPlayerPlayFilled style={{ width: rem(14), height: rem(14) }} />}
              onClick={() => {
                if (canRunTestsLoading) {
                  notifications.show({
                    message: 'Loading...Please try again in a few seconds',
                    color: 'gray',
                  });
                } else if (!canRunTests) {
                  modals.open({
                    size: 'sm',
                    centered: true,
                    children: <CanNotStartTesting />,
                  });
                } else {
                  modals.open({
                    title: 'Manual Trigger',
                    size: '60%',
                    children: <ManualTriggerAdvanced testSuiteId={testSuiteId} />,
                  });
                }
              }}
            >
              Run
            </Button>

            <Menu
              shadow="md"
              position="bottom-end"
            >
              <Menu.Target>
                <ActionIcon
                  size={25}
                  variant="transparent"
                  title="Test Suite Actions"
                  className="text-gray-700 dark:text-gray-400"
                >
                  <IconDotsVertical size={22} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {/* Fetches full test suite content and triggers CSV download */}
                <Menu.Item
                  leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}
                  disabled={isExportLoading}
                  onClick={async () => {
                    const exportDataResponse = await fetchExportData();
                    if (exportDataResponse.data) {
                      downloadTestSuiteCsv(exportDataResponse.data);
                      notifications.show({
                        message: 'CSV downloaded successfully',
                        color: 'teal',
                      });
                    } else {
                      notifications.show({
                        message: 'Failed to export test suite',
                        color: 'red',
                      });
                    }
                  }}
                >
                  {isExportLoading ? 'Exporting...' : 'Download CSV'}
                </Menu.Item>
                {isAdmin ?
                  <Menu.Item
                    color="orange"
                    leftSection={<IconTrash size={14} />}
                    onClick={async () => {
                      await clearRunHistory.mutateAsync({ projectId: projectId!, testSuiteId });
                      trpcUtils.testSuite.get.invalidate().catch(() => undefined);
                    }}
                  >
                    Clear History
                  </Menu.Item>
                : null}
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                  onClick={() =>
                    modals.openConfirmModal({
                      title: 'Please confirm',
                      children: <span>This will permanently delete the &apos;{testSuite?.name}&apos; test suite.</span>,
                      labels: { confirm: 'Delete Suite', cancel: 'Cancel' },
                      confirmProps: {
                        color: 'red',
                      },
                      onConfirm: () => {
                        deleteSuite({ projectId: projectId!, testSuiteId });
                      },
                    })
                  }
                >
                  Delete Test Suite
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        </div>
        <EditableTextArea
          className="mb-3 w-full text-sm text-gray-600"
          initialVal={testSuite?.description || ''}
          textStyle="text-sm text-gray-500"
          placeHolder="Add description"
          tooltipPosition="top-end"
          handleSave={async (newVal) => {
            await updateSuite({ id: testSuiteId, projectId: projectId!, data: { description: newVal } });
          }}
        />

        {/* Use this test suite to run sets of tests together on your CI */}
        <Tabs
          value={tab}
          onChange={setTab}
          className="flex flex-grow flex-col overflow-hidden"
        >
          <Tabs.List>
            <Tabs.Tab value="tests">Tests</Tabs.Tab>
            <Tabs.Tab value="runs">
              <div className="flex items-center gap-1">
                <span>Runs</span>
                {!!latestRunStatus && (
                  <span className="flex h-0 items-center">
                    {/* h-0 makes the span have no height but still renders the icon, prevents layout shift when icon loads in */}
                    <StatusIcon
                      status={latestRunStatus}
                      filled
                    />
                  </span>
                )}
              </div>
            </Tabs.Tab>
            <Tabs.Tab value="heatmap">Heat Map</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel
            className="h-full overflow-auto"
            value="tests"
          >
            <TestSuiteTestsContainer />
          </Tabs.Panel>
          <Tabs.Panel
            className="h-full overflow-auto"
            value="runs"
          >
            <TestSuiteResultView onLatestRunStatusChange={setLatestRunStatus} />
          </Tabs.Panel>
          <Tabs.Panel
            className="h-full overflow-auto"
            value="heatmap"
          >
            <TestSuiteHeatMap
              testSuiteId={testSuiteId}
              isActive={tab === 'heatmap'}
            />
          </Tabs.Panel>
        </Tabs>
      </div>
      <TestRunDrawer />

      <Modal
        opened={!!testSuite && settingsModalOpened}
        onClose={() => {
          setSettingsModalOpened(false);
          modals.closeAll();
        }}
        title="Suite Settings"
        size="auto"
        centered
      >
        {!!testSuite && (
          <SuiteSettings
            onClose={() => setSettingsModalOpened(false)}
            testSuite={testSuite}
          />
        )}
      </Modal>
    </>
  );
}
